/**
 * Environment Configuration for EASI 2.0 Frontend
 *
 * All environment variables for AWS services.
 * Values are set in the AWS Amplify Console under Environment Variables.
 *
 * SECURITY NOTE: Never commit actual values to this file.
 * All sensitive configuration comes from environment variables.
 */

// Validate required environment variables
const requiredEnvVars = [
  'REACT_APP_API_ENDPOINT',
  'REACT_APP_COGNITO_USER_POOL_ID',
  'REACT_APP_COGNITO_CLIENT_ID',
] as const;

// Check for missing required variables in production
if (process.env.NODE_ENV === 'production') {
  const missing = requiredEnvVars.filter((key) => !process.env[key]);
  if (missing.length > 0) {
    console.error(`Missing required environment variables: ${missing.join(', ')}`);
  }
}

/**
 * Application configuration derived from environment variables.
 */
export const config = {
  /**
   * Application environment
   */
  environment: process.env.NODE_ENV || 'development',
  isProduction: process.env.NODE_ENV === 'production',
  isDevelopment: process.env.NODE_ENV === 'development',

  /**
   * API Gateway endpoint
   * Set in Amplify Console: REACT_APP_API_ENDPOINT
   */
  apiEndpoint: process.env.REACT_APP_API_ENDPOINT || '',

  /**
   * Lambda Function URL for MySLP chat — bypasses API Gateway 29s timeout.
   * Falls back to API Gateway if empty.
   */
  myslpFunctionUrl: process.env.REACT_APP_MYSLP_FUNCTION_URL || '',

  /**
   * AWS Cognito configuration
   * Set in Amplify Console
   */
  cognito: {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
    region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
  },

  /**
   * S3 configuration for direct uploads (presigned URLs)
   * Set in Amplify Console
   */
  s3: {
    bucket: process.env.REACT_APP_S3_BUCKET || '',
    region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
  },

  /**
   * Security settings for HIPAA/FERPA compliance
   */
  security: {
    // Session inactivity timeout — HIPAA §164.312(a)(2)(iii) requires automatic logoff.
    // 7 minutes idle = auto-logout. Warning at 5 minutes. API activity resets the timer
    // (axios interceptors dispatch easi-api-activity events → SecurityWrapper resets).
    sessionTimeoutMs: 7 * 60 * 1000,

    // Warning before session timeout (at 5 minutes idle, 2 minutes before logout)
    sessionWarningMs: 2 * 60 * 1000,

    // Maximum failed login attempts before lockout
    maxLoginAttempts: 5,

    // Lockout duration in milliseconds (30 minutes)
    lockoutDurationMs: 30 * 60 * 1000,

    // Require HTTPS in production
    requireHttps: process.env.NODE_ENV === 'production',
  },

  /**
   * Feature flags
   */
  features: {
    enableAudioRecording: process.env.REACT_APP_ENABLE_AUDIO_RECORDING === 'true',
    enableRealtimeTranscription: process.env.REACT_APP_ENABLE_REALTIME === 'true',
    enableDebugMode: process.env.REACT_APP_DEBUG === 'true',
    enableVoice: process.env.REACT_APP_ENABLE_VOICE === 'true',
    enableReportWizard: process.env.REACT_APP_REPORT_WIZARD === 'true',
    // Phase 1E Stage 2 — email-magic-link 2FA. Default OFF; flipping on
    // enables the 2FA challenge after Cognito password authentication for
    // users with `custom:twofa_method=email_magic_link` in the ID token.
    twofaEnabled: process.env.REACT_APP_TWOFA_ENABLED === 'true',
  },
} as const;

/**
 * Amplify configuration object for initialization
 */
export const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: config.cognito.userPoolId,
      userPoolClientId: config.cognito.clientId,
    },
  },
};

export default config;
